import React, { useMemo, useState, useCallback } from 'react'
import { pdf } from '@react-pdf/renderer'
import { useQuery } from '@tanstack/react-query'
import utilsMergePdfs from 'report/utils/mergePdfs'

const emptyBlobsArray: Readonly<Blob[]> = []

export type RenderProgress = {
  currentPdf: number
  totalPdfs: number
  isRendering: boolean
}

type ProgressCallback = (current: number, total: number) => void

const getReactPDFBuffers = async (
  components: React.JSX.Element[],
  onProgress?: ProgressCallback
): Promise<ArrayBuffer[]> => {
  const buffers: ArrayBuffer[] = []
  const batchSize = 5

  for (let i = 0; i < components.length; i += batchSize) {
    const batch = components.slice(i, i + batchSize)
    const batchBuffers = await Promise.all(
      batch.map(async (component) =>
        (await pdf(component).toBlob()).arrayBuffer()
      )
    )
    buffers.push(...batchBuffers)

    if (onProgress) {
      onProgress(buffers.length, components.length)
    }
  }

  return buffers
}

export type UseReportPdfsProps = {
  components: React.JSX.Element[]
  sourceObjects: object[]
  shouldMerge: boolean
  getPdfBuffers?: typeof getReactPDFBuffers
  mergePdfs?: typeof utilsMergePdfs
  enabled?: boolean
}

export default function useReportPdfs({
  components,
  sourceObjects,
  shouldMerge,
  getPdfBuffers = getReactPDFBuffers,
  mergePdfs = utilsMergePdfs,
  enabled
}: UseReportPdfsProps) {
  const [renderProgress, setRenderProgress] = useState<RenderProgress>({
    currentPdf: 0,
    totalPdfs: 0,
    isRendering: false
  })

  const handleProgress = useCallback((current: number, total: number) => {
    setRenderProgress({
      currentPdf: current,
      totalPdfs: total,
      isRendering: current < total
    })
  }, [])

  const {
    data: blobs = emptyBlobsArray,
    isFetching: isRendering,
    isFetched: isRenderFinished,
    error
  } = useQuery({
    queryKey: [
      'useReportPdfs',
      shouldMerge ? 'merge' : 'split',
      ...sourceObjects
    ],
    queryFn: async () => {
      setRenderProgress({
        currentPdf: 0,
        totalPdfs: components.length,
        isRendering: true
      })

      const pdfBuffers = await getPdfBuffers(components, handleProgress)

      if (pdfBuffers.length === 0) {
        return []
      }

      const finishedBuffers = shouldMerge
        ? [await mergePdfs(pdfBuffers)]
        : pdfBuffers

      setRenderProgress((prev) => ({
        ...prev,
        isRendering: false
      }))

      return finishedBuffers.map(
        (finishedBuffer) =>
          new Blob([finishedBuffer as BlobPart], {
            type: 'application/pdf'
          })
      )
    },
    retry: false,
    enabled
  })

  const urls = useMemo(
    () => blobs.map((blob) => URL.createObjectURL(blob)),
    [blobs]
  )

  return {
    blobs,
    urls,
    isRendering,
    isRenderFinished,
    error,
    renderProgress
  }
}
